import * as React from 'react'
import { Listbox as HeadlessListbox, Transition } from '@headlessui/react'
import { ChevronDown, Check } from 'lucide-react'
import { clsx } from 'clsx'

// Forward declare ListboxLabel for type checking
export interface ListboxLabelProps {
  children: React.ReactNode
  className?: string
}

export const ListboxLabel: React.FC<ListboxLabelProps> = ({ children, className }) => {
  return <span className={className}>{children}</span>
}

ListboxLabel.displayName = 'ListboxLabel'

export interface ListboxDescriptionProps {
  children: React.ReactNode
  className?: string
}

export const ListboxDescription: React.FC<ListboxDescriptionProps> = ({ children, className }) => {
  return (
    <span className={clsx('text-xs text-neutral-500 dark:text-neutral-400', className)}>
      {children}
    </span>
  )
}

export interface ListboxProps {
  name?: string
  value?: any
  defaultValue?: any
  onChange?: (value: any) => void
  disabled?: boolean
  invalid?: boolean
  placeholder?: string
  children: React.ReactNode
  className?: string
}

export const Listbox = React.forwardRef<HTMLButtonElement, ListboxProps>(
  ({ name, value, defaultValue, onChange, disabled = false, invalid = false, placeholder, children, className }, ref) => {
    // Helper to extract label from selected option
    const getDisplayValue = (selectedValue: any) => {
      if (!selectedValue) return null
      
      let displayText: React.ReactNode = null
      React.Children.forEach(children, (child: any) => {
        if (React.isValidElement(child) && child.props.value === selectedValue) {
          // Try to find ListboxLabel first
          const childrenArray = React.Children.toArray(child.props.children)
          const labelComponent = childrenArray.find(
            (c: any) => React.isValidElement(c) && (c.type === ListboxLabel || c.type?.displayName === 'ListboxLabel')
          )
          
          if (labelComponent && React.isValidElement(labelComponent)) {
            displayText = labelComponent.props.children
          } else {
            // Use first text child
            displayText = childrenArray.find((c: any) => typeof c === 'string' || (React.isValidElement(c) && c.type !== ListboxDescription)) || childrenArray[0]
          }
        }
      })
      
      return displayText
    }

    return (
      <HeadlessListbox value={value} defaultValue={defaultValue} onChange={onChange} disabled={disabled} name={name}>
        {({ open, value: selectedValue }) => {
          const displayValue = getDisplayValue(selectedValue)
          
          return (
            <div className={clsx('relative', className)}>
              <HeadlessListbox.Button
                ref={ref}
                className={clsx(
                  'relative w-full cursor-default rounded-xl border py-2 pl-3 pr-10 text-left text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-accent-400 focus:ring-offset-0 focus:shadow-glow',
                  invalid
                    ? 'border-danger-500 focus:ring-danger-500'
                    : 'border-neutral-200 dark:border-white/10',
                  disabled
                    ? 'bg-neutral-50 dark:bg-white/5 text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
                    : 'bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200',
                  className
                )}
              >
                <span className="block truncate">
                  {displayValue || <span className="text-neutral-400 dark:text-neutral-500">{placeholder || 'Select...'}</span>}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDown
                    className={clsx(
                      'h-5 w-5 transition-transform',
                      open && 'rotate-180',
                      disabled ? 'text-neutral-400' : 'text-neutral-400 dark:text-neutral-500'
                    )}
                    aria-hidden="true"
                  />
                </span>
              </HeadlessListbox.Button>

            <Transition
              as={React.Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <HeadlessListbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-brand-900/95 backdrop-blur shadow-soft py-1 text-sm focus:outline-none">
                {children}
              </HeadlessListbox.Options>
            </Transition>
          </div>
          )
        }}
      </HeadlessListbox>
    )
  }
)

Listbox.displayName = 'Listbox'

export interface ListboxOptionProps {
  value: any
  disabled?: boolean
  children: React.ReactNode
}

export const ListboxOption: React.FC<ListboxOptionProps> = ({ value, disabled = false, children }) => {
  return (
    <HeadlessListbox.Option
      value={value}
      disabled={disabled}
      className={({ active, selected }) =>
        clsx(
          'relative cursor-default select-none py-2 pl-3 pr-9',
          active && !disabled
            ? 'bg-primary-600/10 dark:bg-primary-600/20 text-primary-600 dark:text-white'
            : 'text-neutral-900 dark:text-neutral-200',
          disabled && 'opacity-50 cursor-not-allowed'
        )
      }
    >
      {({ selected }) => (
        <>
          <span className={clsx('block truncate', selected ? 'font-medium' : 'font-normal')}>
            {children}
          </span>
          {selected && (
            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600 dark:text-primary-400">
              <Check className="h-5 w-5" aria-hidden="true" />
            </span>
          )}
        </>
      )}
    </HeadlessListbox.Option>
  )
}


